import Warning from '../../core/Warning';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { makeCancelable, sortObjects } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import Concept from './Concept';

const CONCEPT_BATCH = 100;

const loadOptions = {
  predicates: [
    "http://www.w3.org/2004/02/skos/core#inScheme",
    "http://www.w3.org/2004/02/skos/core#prefLabel",
    "http://www.w3.org/2002/07/owl#deprecated",
    "http://purl.org/dc/terms/title",
    "http://www.w3.org/2008/05/skos-xl#prefLabel",
    "http://www.w3.org/2004/02/skos/core#narrower",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
    "https://grafsync.graphifi.com/schema/gs/status"
  ],
  includeAuth: true,
  tests: [
    {
      id: "http://www.w3.org/2008/05/skos-xl#prefLabel",
      join: [{
        predicate: "http://www.w3.org/2008/05/skos-xl#literalForm"
      }]
    }
  ]
};

class TaxonomyStructure extends Component {

  constructor(props) {
    super(props);
    this.state = {
      showNumber: CONCEPT_BATCH,
      concepts: [],
      warning: "",
      viewLang: null,
      isLoading: props.concepts && props.concepts.length > 0,
      isLoaded: !(props.concepts && props.concepts.length > 0),
      isErrored: null
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.collectData();
  }

  timeMoreConcepts = () => {
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
    if (this.state.showNumber < this.state.concepts.length) {
      this.moreTimeout = setTimeout(() => {
        this.showMore();
      }, 1000);
    }
  };

  shouldComponentUpdate() {
    return true;
  }

  static getDerivedStateFromProps(props, state) {
    if ((props.concepts && props.concepts.length > 0 && props.data._version !== state.version)
      || (props.viewLang !== state.viewLang)) {
      return {
        isLoading: true,
        isLoaded: false,
        viewLang: props.viewLang,
        ...(props.viewLang !== state.viewLang) && { concepts: [] },
        version: props.data._version
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isLoaded) this.collectData();
    this.timeMoreConcepts();
  }

  collectData() {
    // We load ALL the concepts necessary at this level in the tree in one API call to avoid unnecessary calls
    if (!this.state.isLoaded && !this.state.isErrored) {
      this.loadData(this.props.concepts)
        .promise
        .then(() => {
          if (this.cancellable && !this.cancellable.isCancelled()) this.sortConcepts();
        })
        .catch((e) => {
          if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
        });
    }
  }

  sortConcepts() {
    const concepts = this.props.concepts.map(id => GS.data(id, this.props.graph));
    const firstLang = this.props.viewLang || this.props.defaultLang;
    const secondLang = this.props.viewLang !== this.props.defaultLang ? this.props.defaultLang : null;
    let sortedConcepts = sortObjects([...concepts], 'prefLabel', firstLang, secondLang, false)
      .map(concept => concept.id);
    this.setState({ isLoading: false, isLoaded: true, concepts: sortedConcepts, showNumber: CONCEPT_BATCH });
  }

  loadData() {
    const { viewLang, defaultLang, graph } = this.props;
    const mergedLoadOptions = Object.assign({}, loadOptions, { language: viewLang && viewLang !== defaultLang ? [defaultLang, viewLang] : defaultLang });    
    this.cancellable = makeCancelable(GS.loadData(this.props.concepts, mergedLoadOptions, graph, this.props.skosXL));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
  }

  monitorConceptUpdate = (iri, property) => {
    if (property === "prefLabel") this.sortConcepts();
  };

  showMore = () => {
    const showNumber = this.state.showNumber + CONCEPT_BATCH;
    this.setState({ showNumber });
  };

  showWarning = (warning) => this.setState({ warning });

  onExitWarning = () => this.setState({ warning: "" });

  render() {

    const { warning, isLoading, concepts, isErrored, showNumber } = this.state;
    const { onSelect, defaultLang, schemeIRI, expandConcept, collapseConcept, foundRef, addToScheme,
       activeConcept, useXL, useSlashIRI, useUuidIRI, iriOptions, useWorkflow,
      onAddExistingConcept, onAddNewConcept, onDeleteConcept, foundTime, titleTree, viewLang, graph, titlePath, selectedTitlePath,
      childProperty, foundPath, onBreakLink, data, path, expandedPaths, isReadOnly, skosXL } = this.props;

    if (isErrored) return <Errored data={isErrored} />;
    if (isLoading && concepts.length === 0) return <Loading />;

    return (
      <React.Fragment>
        {warning !== "" && <Warning message={warning} title="Action Not Permitted" onExitWarning={this.onExitWarning} />}
        {concepts.slice(0, showNumber).map(concept => {
          // Filter paths to only send those relevant to each concept
          const expandedSubpaths = expandedPaths.filter(subPath => subPath.startsWith(path + "<" + concept + ">"));
          return <Concept
            key={concept}
            id={concept}
            graph={graph}
            activeConcept={activeConcept}
            onSelect={onSelect}
            selectedTitlePath={selectedTitlePath}
            titlePath={titlePath}
            titleTree={titleTree}
            iriOptions={iriOptions}
            skosXL={skosXL}
            showWarning={this.showWarning}
            useXL={useXL}
            useWorkflow={useWorkflow}
            useSlashIRI={useSlashIRI}
            useUuidIRI={useUuidIRI}   
            expandedPaths={expandedSubpaths}
            path={path + "<" + concept + ">"}
            addToScheme={addToScheme}
            onBreakLink={onBreakLink}
            childProperty={childProperty}
            onAddNewConcept={onAddNewConcept}
            onDeleteConcept={onDeleteConcept}
            onAddExistingConcept={onAddExistingConcept}
            expandConcept={expandConcept}
            collapseConcept={collapseConcept}
            parent={data.id}
            isReadOnly={isReadOnly}
            foundRef={foundRef}
            foundTime={foundTime}
            foundPath={foundPath}
            schemeIRI={schemeIRI}
            defaultLang={defaultLang}
            viewLang={viewLang}
            loadOptions={{
              language: viewLang && viewLang !== defaultLang ? [defaultLang, viewLang] : defaultLang
            }}
            updateEvent={this.monitorConceptUpdate}
          />;
        }
        )}
      </React.Fragment>
    );
  }

}

TaxonomyStructure.propTypes = {
  schemeIRI: PropTypes.string.isRequired,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  version: PropTypes.number,
  path: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool,
  expandedPaths: PropTypes.arrayOf(PropTypes.string).isRequired,
  expandConcept: PropTypes.func.isRequired,
  collapseConcept: PropTypes.func.isRequired,
  onAddExistingConcept: PropTypes.func.isRequired,
  onAddNewConcept: PropTypes.func.isRequired,
  onDeleteConcept: PropTypes.func.isRequired,
  selectedTitlePath: PropTypes.array,
  foundTime: PropTypes.number,
  skosXL: PropTypes.bool.isRequired,
  useXL: PropTypes.bool,
  useWorkflow: PropTypes.bool,
  useSlashIRI: PropTypes.bool,
  useUuidIRI: PropTypes.bool,
  foundPath: PropTypes.string,
  childProperty: PropTypes.string.isRequired,
  showWarning: PropTypes.func,
  graph: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  titleTree: PropTypes.array,
  concepts: PropTypes.arrayOf(PropTypes.string),
  onSelect: PropTypes.func.isRequired,
  addToScheme: PropTypes.func.isRequired,
  onBreakLink: PropTypes.func.isRequired,
  iriOptions: PropTypes.object
};

export default TaxonomyStructure;